import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  Link2,
  Loader2,
  ArrowLeft,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';
import { teamsPopupReturnUrl } from '../utility/teamsPopupReturn';
import { getInitialsFromName } from '@/utility/utility';
import { SlackLogo } from './SlackLogo';

export const SLACK_TOKEN_KEY = 'slack_link_token';

export function Countdown({
  seconds,
  onDone,
}: {
  seconds: number;
  onDone: () => void;
}) {
  const [left, setLeft] = useState(seconds);

  useEffect(() => {
    if (left <= 0) {
      onDone();
      return;
    }
    const t = setTimeout(() => setLeft((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [left, onDone]);

  return <span className="font-mono font-bold text-blue-600">{left}</span>;
}
// ─── shared page ─────────────────────────────────────────────────────────────

export interface SlackLinkPageProps {
  /**
   * Called with the raw Slack JWT once it has been retrieved from
   * sessionStorage. The caller is responsible for making the API request
   * (employee vs admin endpoint). Throw on failure — the error message will
   * be shown to the user.
   */
  onLink: (slackToken: string) => Promise<void>;
  /**
   * Where to send the user after a successful link or when they click the
   * "Go to Dashboard" / "Back" buttons.
   * Defaults to '/dashboard'.
   */
  dashboardPath?: string;
  /**
   * i18n key prefix within the slack namespace.
   * - Employee portal: 'slackLink'     (default)
   * - Admin portal:    'adminSlackLink'
   */
  i18nPrefix?:
    | 'slackLink'
    | 'adminSlackLink'
    | 'workspaceLink'
    | 'teamsLink'
    | 'adminTeamsLink';
  companyName?: string | null;
  companyLogoUrl?: string | null;
  userAvatarUrl?: string | null;
  userInitials?: string | null;
  /** Defaults to the Slack mark; pass another to reuse this page per provider. */
  logo?: ReactNode;
  /** Translation namespace holding `i18nPrefix`. Defaults to the slack one. */
  namespace?: string;
  /** sessionStorage key — must differ per provider so two flows can't collide. */
  tokenKey?: string;
  /** Opt in to reporting success back to a Teams popup instead of showing the
   * success screen. Ignored unless this page really was opened as one. */
  teamsPopupAware?: boolean;
  /**
   * Ask before linking instead of firing on mount.
   *
   * Opt-in, because linking from a deep link the user just clicked is its own
   * confirmation — but the Teams tab opens this in a popup mid-session, where
   * an account being claimed with no prompt reads as something happening *to*
   * you. Needs `<prefix>.confirm.{title,description,action,cancel}`.
   */
  confirmFirst?: boolean;
}

type Status = 'confirm' | 'loading' | 'success' | 'error' | 'missing';

export default function SlackLinkPage({
  onLink,
  dashboardPath = '/dashboard',
  i18nPrefix = 'slackLink',
  companyName,
  companyLogoUrl,
  userAvatarUrl,
  userInitials,
  logo,
  namespace = TranslationNS.slack,
  tokenKey = SLACK_TOKEN_KEY,
  teamsPopupAware = false,
  confirmFirst = false,
}: SlackLinkPageProps) {
  const { t } = useTranslation(namespace);
  const navigate = useNavigate();

  // Starts on the confirm panel when there is one, so it does not flash the
  // spinner first.
  const [status, setStatus] = useState<Status>(
    confirmFirst ? 'confirm' : 'loading'
  );
  const [errorMsg, setErrorMsg] = useState('');
  const calledRef = useRef(false);
  const startedRef = useRef(false);
  // Read on first render: the effect below strips the query along with the
  // fragment token.
  const [popupReturnUrl] = useState(() =>
    teamsPopupAware ? teamsPopupReturnUrl() : null
  );

  const goToDashboard = () => navigate(dashboardPath);

  const runLink = useCallback(() => {
    const token = sessionStorage.getItem(tokenKey);
    if (!token) {
      setStatus('missing');
      return;
    }

    if (calledRef.current) return;
    calledRef.current = true;
    setStatus('loading');

    onLink(token)
      .then(() => {
        sessionStorage.removeItem(tokenKey);
        if (popupReturnUrl) {
          window.location.replace(popupReturnUrl);
          return;
        }
        toast.success(t(`${i18nPrefix}.toast.success`));
        setStatus('success');
      })
      .catch((err: { response?: { data?: { message?: string } } }) => {
        sessionStorage.removeItem(tokenKey);
        const msg =
          err?.response?.data?.message ?? t(`${i18nPrefix}.error.fallback`);
        toast.error(msg);
        setErrorMsg(msg);
        setStatus('error');
      });
    // onLink is a new function reference every render — intentionally excluded
    // so this stays stable (calledRef guards against re-runs anyway).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, i18nPrefix, tokenKey, popupReturnUrl]);

  useEffect(() => {
    // Once only. `t` gets a new identity when the namespace finishes loading,
    // and re-running this after the token has been spent would report a
    // finished link as an invalid one.
    if (startedRef.current) return;
    startedRef.current = true;

    // Pull token out of the URL fragment and stash it so it survives a
    // login redirect. This happens whether or not we link straight away —
    // the token has to outlive the fragment either way.
    const hash = window.location.hash;
    const match = hash.match(/[#&]token=([^&]*)/);
    if (match) {
      sessionStorage.setItem(tokenKey, decodeURIComponent(match[1]));
      window.history.replaceState(null, '', window.location.pathname);
    }

    if (!sessionStorage.getItem(tokenKey)) {
      toast(t(`${i18nPrefix}.toast.invalid_link`));
      setStatus('missing');
      return;
    }

    if (confirmFirst) {
      setStatus('confirm');
      return;
    }
    runLink();
  }, [t, i18nPrefix, tokenKey, confirmFirst, runLink]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white border border-gray-200 rounded-2xl shadow-lg p-8">
        {/* Header */}
        <div className="flex items-center justify-center gap-3 mb-8">
          {logo ?? <SlackLogo size={45} />}
          <span className="w-6 h-px bg-gray-300" />

          {/* Company */}
          {companyLogoUrl ? (
            <img
              src={companyLogoUrl}
              alt={companyName ?? 'Company'}
              className="w-14 h-14 rounded-xl object-contain border border-gray-100 bg-white p-1"
            />
          ) : (
            <div className="w-14 h-14 rounded-xl bg-blue-600 flex items-center justify-center text-white font-bold text-lg">
              {getInitialsFromName(companyName ?? '')}
            </div>
          )}

          {/* User — employee portal only */}
          {(userAvatarUrl || userInitials) && (
            <>
              <span className="w-6 h-px bg-gray-300" />
              {userAvatarUrl ? (
                <img
                  src={userAvatarUrl}
                  alt={userInitials ?? ''}
                  className="w-14 h-14 rounded-xl object-cover border border-gray-100"
                />
              ) : (
                <div className="w-14 h-14 rounded-xl bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-lg">
                  {userInitials}
                </div>
              )}
            </>
          )}
        </div>

        {/* CONFIRM — only when the caller asked to be asked first */}
        {status === 'confirm' && (
          <div className="flex flex-col items-center text-center gap-4">
            <div className="w-16 h-16 rounded-full bg-blue-50 border flex items-center justify-center">
              <Link2 size={30} className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {t(`${i18nPrefix}.confirm.title`)}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {t(`${i18nPrefix}.confirm.description`)}
              </p>
            </div>
            <button
              onClick={runLink}
              className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold"
            >
              {t(`${i18nPrefix}.confirm.action`)}
            </button>
            <button
              onClick={goToDashboard}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              {t(`${i18nPrefix}.confirm.cancel`)}
            </button>
          </div>
        )}

        {/* LOADING */}
        {status === 'loading' && (
          <div className="flex flex-col items-center text-center gap-4">
            <Loader2 size={40} className="text-blue-500 animate-spin" />
            <div>
              <h2 className="text-lg font-semibold">
                {t(`${i18nPrefix}.loading.title`)}
              </h2>
              <p className="text-sm text-gray-500">
                {t(`${i18nPrefix}.loading.description`)}
              </p>
            </div>
          </div>
        )}

        {/* SUCCESS */}
        {status === 'success' && (
          <div className="flex flex-col items-center text-center gap-4">
            <div className="w-16 h-16 rounded-full bg-emerald-50 border flex items-center justify-center">
              <CheckCircle2 size={32} className="text-emerald-500" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {t(`${i18nPrefix}.success.title`)}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {t(`${i18nPrefix}.success.description`)}
              </p>
            </div>
            <p className="text-xs text-gray-400">
              Redirecting in <Countdown seconds={4} onDone={goToDashboard} />{' '}
              {t(`${i18nPrefix}.success.redirecting_suffix`)}
            </p>
            <button
              onClick={goToDashboard}
              className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold"
            >
              {t(`${i18nPrefix}.success.go_to_dashboard`)}
            </button>
          </div>
        )}

        {/* ERROR */}
        {status === 'error' && (
          <div className="flex flex-col items-center text-center gap-4">
            <div className="w-16 h-16 rounded-full bg-red-50 border flex items-center justify-center">
              <XCircle size={32} className="text-red-500" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {t(`${i18nPrefix}.error.title`)}
              </h2>
              <p className="text-sm text-gray-500">{errorMsg}</p>
            </div>
            <button
              onClick={goToDashboard}
              className="w-full flex items-center justify-center gap-2 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
            >
              <ArrowLeft size={14} />
              {t(`${i18nPrefix}.error.back`)}
            </button>
          </div>
        )}

        {/* MISSING */}
        {status === 'missing' && (
          <div className="flex flex-col items-center text-center gap-4">
            <div className="w-16 h-16 rounded-full bg-amber-50 border flex items-center justify-center">
              <AlertCircle size={32} className="text-amber-500" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {t(`${i18nPrefix}.missing.title`)}
              </h2>
              <p className="text-sm text-gray-500">
                {t(`${i18nPrefix}.missing.description`)}
              </p>
            </div>
            <button
              onClick={goToDashboard}
              className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
            >
              {t(`${i18nPrefix}.missing.back`)}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
